import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormHelperText,
  Grid2,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { UserOrEmailMapping } from "../../features/Admin/typings/admin.types";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import {
  BookingType,
  type VisitorRegistrationEntry
} from "../../features/Booking-Form/typings/booking-inputs";
import {
  BR,
  Company,
  RestrictionZone,
  UserZoneBookingObject
} from "../../features/Booking-Form/typings/booking.types";
import { PricingModel } from "../../features/Login/typings/login.types";
import { useRemoteFetchUserInfoForAllUsers } from "../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { getOptionLabelOfUserOrEmailMapping } from "../../utils/type/type.utils";
import FormDialog, { MS } from "../Dialog/form-dialog.component";
import ConfirmZoneRestrictionInputs from "../Dialog/partials/inputs-confirm-restriction.component";
import RestrictionPicker from "../Pickers/restriction-picker.component";
import { InfoIconWithTooltip } from "../Title/InfoIcon";
import { handleActivityBasedRestriction, handleDialogRestriction } from "./utils/booking.utils";
import { checkActivityBased, handleSelectionZoneRestriction } from "./functions/form.functions";
import MsTeamsCallSwitch from "./msTeams-call-switch.component";
import {
  filterParticipantsOpt,
  handleClearRequiredOpt,
  handleSelectOptionalOpt,
  handleSelectOrRemoveRequiredOpt,
  isOptionEqualToVal,
  mergeRequiredAndOptionalUsers,
  renderParticiapantTags
} from "./functions/form-zone-restriction.functions";
import { TeamVisitorInvitationRegistrationsPanel } from "./partials/team-visitor-invitation-registrations-panel.component";

/**
 * @description Component which displays the restriction picker and lets the user to fill the zone access with colleagues from the selected restriction object.
 * @version 0.1.0
 */

type P = {
  nextStep: () => void;
  title: string;
  adjustedData: any;
};

const CONFERENCE_SECTION_PAPER_SX = { p: 2, width: "100%", mb: 2 } as const;

const FormZoneRestriction: React.FC<P> = ({ nextStep, adjustedData }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const {
    userInformation: {
      sub,
      pricingModels,
      msGraphEnabled,
      visitorManagementEnabled,
      company: {
        meta: { calendarSyncPossible }
      }
    }
  } = useSelector((state: RootState) => state.login);

  const [users, setUsers] = useState<UserOrEmailMapping[]>([]);
  const [teamRestrictions, setTeamRestrictions] = useState<RestrictionZone>({
    company: inputs.restrictionObject ? inputs.restrictionObject.company : ({} as Company),
    object: inputs.restrictionObject
      ? inputs.restrictionObject.object
      : ({} as UserZoneBookingObject)
  });

  const [conflicts, setConflicts] = useState<BR[]>([]);
  const [modal, setModal] = useState<MS<BR[]>>({
    isOpen: false,
    data: undefined
  });

  const [requiredUsers, setRequiredUsers] = useState<UserOrEmailMapping[]>([]);
  const [optionalUsers, setOptionalUsers] = useState<UserOrEmailMapping[]>([]);
  const [optionalUsersOptions, setOptionalUsersOptions] = useState<UserOrEmailMapping[]>([]);
  const [showOptionalParticipants, setShowOptionalParticipants] = useState(false);
  const [visitorInvitationExpanded, setVisitorInvitationExpanded] = useState(false);

  const { data: remoteUsers, isFetching: isRemoteUsersFetching } =
    useRemoteFetchUserInfoForAllUsers();

  const isConferenceMode = inputs.mode === BookingType.CONFERENCEZONE;

  const showConferenceVisitorInvitation = isConferenceMode && visitorManagementEnabled === true;

  const initialVisitorRegistrations = inputs.visitorRegistrations?.length
    ? inputs.visitorRegistrations
    : undefined;

  const closeVisitorInvitation = useCallback(() => {
    setVisitorInvitationExpanded(false);
  }, []);

  const handleConferenceVisitorDraftChange = useCallback(
    (regs: VisitorRegistrationEntry[]) => {
      dispatch(setInputs({ visitorRegistrations: regs }));
    },
    [dispatch]
  );

  const handleOptionalClick = () => {
    setShowOptionalParticipants(!showOptionalParticipants);
    if (!showOptionalParticipants) {
      const unselectedOptionalUsers = users.filter(
        user => !requiredUsers.some(requiredUser => requiredUser.userId === user.userId)
      );
      setOptionalUsersOptions(unselectedOptionalUsers);
    }
  };

  useEffect(() => {
    if (inputs.zoneAccess && inputs.zoneAccess.length > 0) {
      const nonSelectedRequiredUsers = optionalUsersOptions.filter(user => {
        return !requiredUsers.some(requiredUser => requiredUser.userId === user.userId);
      });
      setOptionalUsersOptions(nonSelectedRequiredUsers);
    }
  }, [inputs.zoneAccess, requiredUsers, optionalUsers]);

  useEffect(() => {
    checkActivityBased(inputs.activityBasedBooking, nextStep);
  }, [inputs.activityBasedBooking, nextStep]);

  useEffect(() => {
    if (remoteUsers)
      handleActivityBasedRestriction(inputs, dispatch, setUsers, users, sub, remoteUsers);
  }, [remoteUsers, isRemoteUsersFetching]);

  useEffect(() => {
    if (adjustedData) {
      setConflicts(adjustedData);
      setModal({ isOpen: true, data: [] });
    }
  }, [adjustedData, inputs.restrictionObject?.object?.type, pricingModels]);

  useEffect(() => {
    if (teamRestrictions.object)
      dispatch(
        setInputs({
          restrictionObject: {
            company: teamRestrictions.company,
            object: teamRestrictions.object
          }
        })
      );
  }, [dispatch, teamRestrictions.company, teamRestrictions.object]);

  const requiredParticipantsAutocomplete = (
    <Autocomplete
      multiple
      disableCloseOnSelect
      id="tags-outlined9"
      data-testid={"autocomplete-required"}
      options={users}
      freeSolo
      filterOptions={filterParticipantsOpt}
      handleHomeEndKeys
      filterSelectedOptions
      style={{ flex: 1, width: "100%" }}
      isOptionEqualToValue={isOptionEqualToVal}
      getOptionLabel={getOptionLabelOfUserOrEmailMapping}
      onChange={(event, values, reason, details) => {
        const valOpt = values as UserOrEmailMapping[];
        const selectedOpt = details?.option as UserOrEmailMapping;

        switch (reason) {
          case "selectOption":
          case "removeOption": {
            handleSelectOrRemoveRequiredOpt({
              state: { valOpt, reason, details, optionalUsers, selectedOpt },
              action: {
                setRequiredUsers,
                setOptionalUsers,
                dispatch,
                setInputs,
                setOptionalUsersOptions,
                enqueueSnackbar
              }
            });
            return;
          }
          case "clear": {
            handleClearRequiredOpt({
              state: { valOpt, optionalUsers, requiredUsers },
              action: { setRequiredUsers, dispatch, setInputs, setOptionalUsersOptions }
            });
          }
        }
      }}
      value={requiredUsers}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          placeholder={
            showOptionalParticipants
              ? t("Add required participants")
              : t("Add participants or email")
          }
        />
      )}
      renderTags={renderParticiapantTags}
    />
  );

  const optionalParticipantsAutocomplete = (
    <Autocomplete
      multiple
      disableCloseOnSelect
      id="tags-outlined-optional"
      data-testid={"autocomplete-optional"}
      options={optionalUsersOptions}
      filterSelectedOptions
      freeSolo
      filterOptions={filterParticipantsOpt}
      handleHomeEndKeys
      style={{ width: "100%" }}
      isOptionEqualToValue={isOptionEqualToVal}
      getOptionLabel={getOptionLabelOfUserOrEmailMapping}
      renderTags={renderParticiapantTags}
      onChange={(event, values, reason, details) => {
        const optionalVal = values as UserOrEmailMapping[];

        switch (reason) {
          case "selectOption":
          case "removeOption": {
            handleSelectOptionalOpt({
              state: { reason, details, optionalVal, requiredUsers },
              action: { setOptionalUsers, dispatch, setInputs, enqueueSnackbar }
            });
            return;
          }
          case "clear": {
            setOptionalUsers(values as UserOrEmailMapping[]);
            const updatedZoneAccess = mergeRequiredAndOptionalUsers(requiredUsers, optionalVal);
            dispatch(setInputs({ zoneAccess: updatedZoneAccess }));
          }
        }
      }}
      value={optionalUsers}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          placeholder={t("Add optional participants or email")}
        />
      )}
    />
  );

  const descriptionTextField = (
    <TextField
      margin="dense"
      id="description"
      label={isConferenceMode ? undefined : t("Description")}
      placeholder={t("Add a description")}
      type="text"
      multiline
      minRows={4}
      maxRows={8}
      slotProps={{
        input: {
          sx: {
            alignItems: "flex-start",
            "&::placeholder": {
              textAlign: "left",
              verticalAlign: "top"
            }
          }
        }
      }}
      fullWidth
      variant="outlined"
      value={inputs.description || ""}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        dispatch(setInputs({ description: event.target.value }))
      }
    />
  );

  const optionalParticipantsToggle = isConferenceMode ? (
    <Button
      onClick={handleOptionalClick}
      data-testid="show-optional-btn"
      variant="text"
      sx={{ textTransform: "none", mt: 1, alignSelf: "flex-start", px: 0 }}
    >
      {showOptionalParticipants
        ? t("_conferenceInvite_hideOptionalParticipants")
        : t("_conferenceInvite_addOptionalParticipants")}
    </Button>
  ) : (
    <Button
      onClick={handleOptionalClick}
      data-testid="show-optional-btn"
      color="primary"
      sx={[
        { marginLeft: 2 },
        showOptionalParticipants ? { display: "none" } : { display: "block" }
      ]}
    >
      {t("Optional")}
    </Button>
  );

  const optionalParticipantsFields = showOptionalParticipants && (
    <Grid2
      sx={{ mt: isConferenceMode ? 1 : 1 }}
      container
      direction={"row"}
      alignItems={"center"}
      justifyContent="space-between"
    >
      {isConferenceMode && (
        <Typography variant="body2" sx={{ width: "100%", mb: 0.5 }}>
          {t("_conferenceInvite_optionalParticipantsLabel")}
        </Typography>
      )}
      {optionalParticipantsAutocomplete}
    </Grid2>
  );

  const conferenceSubjectField = (
    <TextField
      margin="dense"
      id="name"
      sx={{ marginBottom: 0, width: { xs: "100%", sm: 300 } }}
      variant="outlined"
      label={t("Conference Subject")}
      type="text"
      placeholder={t("Add Subject")}
      data-testid="booking-conference-subject-textfield"
      value={inputs.subject || ""}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        dispatch(setInputs({ subject: event.target.value }))
      }
      error={!inputs.subject}
    />
  );

  const conferenceSubjectHelper = !inputs.subject ? (
    <FormHelperText error sx={{ ml: 1.75, mt: 0, width: { xs: "100%", sm: 300 } }}>
      {t("Subject is required")}
    </FormHelperText>
  ) : null;

  return (
    <Fragment>
      <Grid2 container direction={"column"} alignItems={"flex-start"} justifyContent={"flex-start"}>
        {inputs.mode === BookingType.PLACEZONE &&
          pricingModels.includes(PricingModel.ENTERPRISE) && (
            <>
              <Grid2 container direction={"row"} alignItems={"center"}>
                <Typography variant={"h5"}>{t("Set your zone restrictions")}</Typography>
                <InfoIconWithTooltip
                  tooltipText={t("Choose a suitable company unit for cost calculation")}
                />
              </Grid2>
              <Grid2
                sx={{ marginTop: 1 }}
                container
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
              >
                <RestrictionPicker
                  userId={sub}
                  mode={true}
                  selection={teamRestrictions}
                  setSelection={setTeamRestrictions}
                  zoneBookingSelectionDto={handleSelectionZoneRestriction(inputs)}
                />
              </Grid2>
              {!teamRestrictions.object.zoneBookingObject && (
                <Typography variant={"body2"} color={"error"}>
                  {t("Please select only one project, workorder or costcenter")}
                </Typography>
              )}
            </>
          )}
      </Grid2>

      {isConferenceMode && (
        <Paper
          variant="outlined"
          sx={CONFERENCE_SECTION_PAPER_SX}
          data-testid="conference-invite-meeting-details"
        >
          <Typography variant="subtitle1" gutterBottom>
            {t("_conferenceInvite_meetingDetailsSection")}
          </Typography>
          <Grid2 container direction="column" rowGap={0}>
            <Grid2
              container
              width="100%"
              direction={{ xs: "column", sm: "row" }}
              alignItems={"end"}
              columnGap={2}
              rowGap={1.5}
            >
              <Grid2 sx={{ width: { xs: "100%", sm: "auto" } }}>{conferenceSubjectField}</Grid2>
              {msGraphEnabled && calendarSyncPossible && (
                <Grid2
                  sx={{
                    display: "flex",
                    width: { xs: "100%", sm: "auto" }
                  }}
                >
                  <MsTeamsCallSwitch enableEdit />
                </Grid2>
              )}
            </Grid2>
            {conferenceSubjectHelper}
          </Grid2>
        </Paper>
      )}

      {isConferenceMode ? (
        <Paper
          variant="outlined"
          sx={CONFERENCE_SECTION_PAPER_SX}
          data-testid="conference-invite-participants-section"
        >
          <Grid2 container direction="row" alignItems="center">
            <Typography variant="subtitle1">
              {t("_conferenceInvite_participantsSection")}
            </Typography>
            <InfoIconWithTooltip tooltipText={t("_chosenColleagueNoAccessZone")} />
          </Grid2>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t("_conferenceInvite_participantsHelper")}
          </Typography>
          {showOptionalParticipants && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {t("_conferenceInvite_requiredParticipantsLabel")}
            </Typography>
          )}
          {requiredParticipantsAutocomplete}
          {optionalParticipantsToggle}
          {optionalParticipantsFields}
        </Paper>
      ) : (
        <>
          <Grid2 container direction={"row"} alignItems={"center"}>
            <Typography variant={"h5"}>
              {inputs.mode === BookingType.PLACEZONE
                ? t("Who should have access to your zone?")
                : t("Who do you want to invite?")}
            </Typography>
            <InfoIconWithTooltip
              tooltipText={
                inputs.mode === BookingType.PLACEZONE
                  ? t("_chosenColleagueAccessZone")
                  : t("_chosenColleagueNoAccessZone")
              }
            />
          </Grid2>
          <Grid2
            sx={{ marginTop: 2, display: "flex" }}
            container
            direction={"row"}
            alignItems={"center"}
            justifyContent="space-between"
          >
            {requiredParticipantsAutocomplete}
            {optionalParticipantsToggle}
          </Grid2>
          {optionalParticipantsFields}
        </>
      )}

      {showConferenceVisitorInvitation && (
        <Paper
          variant="outlined"
          sx={CONFERENCE_SECTION_PAPER_SX}
          data-testid="conference-visitor-invitation-section"
        >
          <Typography variant="subtitle1">{t("_conferenceInvite_visitorsSection")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
            {t("_conferenceInvite_visitorsHelper")}
          </Typography>
          <Button
            data-testid="conference-booking-visitor-invitation-btn"
            onClick={() => {
              if (visitorInvitationExpanded) {
                closeVisitorInvitation();
              } else {
                setVisitorInvitationExpanded(true);
              }
            }}
            startIcon={visitorInvitationExpanded ? <RemoveIcon /> : <AddIcon />}
            sx={{ textTransform: "none", mb: 1 }}
          >
            {visitorInvitationExpanded
              ? t("_conferenceInvite_hideVisitors")
              : t("_conferenceInvite_addVisitors")}
          </Button>
          <TeamVisitorInvitationRegistrationsPanel
            bookerSub={sub}
            derivedVisitOnlyLocationLabel={inputs.locationName}
            hideVisitOnlyLocation
            initialRegistrations={initialVisitorRegistrations}
            onDraftChange={handleConferenceVisitorDraftChange}
            panelDataTestId="conference-visitor-invitation-panel"
            showFooterActions={false}
            visitOnlyOnly
            visible={visitorInvitationExpanded}
          />
        </Paper>
      )}

      {isConferenceMode ? (
        <Paper
          variant="outlined"
          sx={CONFERENCE_SECTION_PAPER_SX}
          data-testid="conferencezone-desc-field-container"
        >
          <Typography variant="subtitle1" gutterBottom>
            {t("_conferenceInvite_descriptionSection")}
          </Typography>
          {descriptionTextField}
        </Paper>
      ) : (
        <Grid2
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{ marginTop: 3 }}
          data-testid="conferencezone-desc-field-container"
        >
          {descriptionTextField}
        </Grid2>
      )}

      <FormDialog
        title={t("Confirm Multiple Zone Bookings")}
        description={t("_bookedMultipleOverlappingZones")}
        primaryButtonLabel={t("Confirm")}
        isOpen={modal.isOpen}
        handleCancel={() => setModal({ isOpen: false, data: [] })}
        handleOk={() => {
          handleDialogRestriction(
            modal,
            setModal,
            dispatch,
            inputs,
            teamRestrictions,
            nextStep,
            pricingModels
          );
        }}
      >
        <ConfirmZoneRestrictionInputs
          modalState={modal}
          setModalState={setModal}
          existingBookings={conflicts}
        />
      </FormDialog>
    </Fragment>
  );
};

export default FormZoneRestriction;
